/**
 * muiThemeConfig.js — CONFIG MUI PINADA (imutável no fluxo de design).
 *
 * FONTE ÚNICA do ambiente de render. Entra no tema por `buildMuiTheme.js`, a função que monta o tema do
 * Admin E do estúdio do `design-package`; o `build:design` também copia este arquivo para o pacote, para o
 * designer ler.
 *
 * Assim **pacote == Admin** (mesmo MUI, mesma tipografia, mesmos defaults de componente) → a View
 * renderiza idêntica nos dois. O canvas do Claude Design é só a mesa de desenho; a VALIDAÇÃO de
 * fidelidade é o pacote rodável (MUI real). Ícones: SEMPRE `@mui/icons-material` (SVG), imutável.
 *
 * ⚠️ Este é o ambiente que o CD deve assumir. Está escrito no brief do design-package como
 *    OBRIGATÓRIO. Não trocar versão de MUI nem set de ícones sem alinhar os dois lados.
 */
export const muiThemeConfig = {
  // ESCALA TIPOGRÁFICA ÚNICA (docs/DESIGN-FLOW.md §4) — desktop + redução no celular. Estava em
  // designTokens.muiTypography e só o Admin a recebia; no pacote um h1 caía no default do MUI (6rem/300).
  // PROIBIDO fontSize/lineHeight cru nas Views: use a variante.
  typography: {
    // Corpo: fonte fixa do painel (D19).
    fontFamily: 'var(--font-family)',
    // D18: a fonte de título do NICHO só entra em h1/h2 (título de página). Card, tabela, campo e
    // botão ficam na fonte de corpo, fixa.
    h1: { fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1.75rem', lineHeight: 1.2, '@media (max-width:600px)': { fontSize: '1.5rem' } },
    h2: { fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.25, '@media (max-width:600px)': { fontSize: '1.375rem' } },
    h3: { fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.3, '@media (max-width:600px)': { fontSize: '1.125rem' } },
    // line-height `normal` (~1.2) em h4–h6, body, subtitle e caption — espelha como o texto se comporta
    // sem override; o `sx` explícito da View sempre vence. (MUI default 1.5/1.66 "afastava" label/hint.)
    h4: { fontWeight: 600, fontSize: '1.5rem', lineHeight: 'normal', '@media (max-width:600px)': { fontSize: '1.25rem' } },
    h5: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 'normal', '@media (max-width:600px)': { fontSize: '1.125rem' } },
    h6: { fontWeight: 600, fontSize: '1.0625rem', lineHeight: 'normal', '@media (max-width:600px)': { fontSize: '0.9375rem' } },
    subtitle1: { fontWeight: 600, fontSize: '1rem', lineHeight: 'normal', '@media (max-width:600px)': { fontSize: '0.9375rem' } },
    subtitle2: { fontWeight: 600, fontSize: '0.875rem', lineHeight: 'normal', '@media (max-width:600px)': { fontSize: '0.8125rem' } },
    body1: { fontSize: '1rem', lineHeight: 'normal', '@media (max-width:600px)': { fontSize: '0.9375rem' } },
    body2: { fontSize: '0.875rem', lineHeight: 'normal', '@media (max-width:600px)': { fontSize: '0.8125rem' } },
    button: { textTransform: 'none', fontSize: '0.875rem', '@media (max-width:600px)': { fontSize: '0.8125rem' } },
    caption: { fontSize: '0.75rem', lineHeight: 'normal', display: 'block', '@media (max-width:600px)': { fontSize: '0.6875rem' } },
    overline: { fontWeight: 600, fontSize: '0.6875rem', lineHeight: 1.4, letterSpacing: '0.04em', textTransform: 'uppercase' },
  },
  components: {
    // Densidade padrão do Admin (compacto) — pareia com o que a designer desenhou.
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiSelect: { defaultProps: { size: 'small' } },
    MuiFormControl: { defaultProps: { size: 'small' } },
    // Toggle "chunky" (como o CD renderiza) — o Switch fino default do MUI ficava apertado.
    MuiSwitch: {
      styleOverrides: {
        root: { width: 44, height: 26, padding: 0, display: 'flex' },
        switchBase: { padding: 3, '&.Mui-checked': { transform: 'translateX(18px)' } },
        thumb: { width: 20, height: 20, boxShadow: '0 1px 2px rgba(0,0,0,.2)' },
        track: { borderRadius: 13, opacity: 1, backgroundColor: 'var(--slate-300)' }, // trilho desligado segue o modo (claro/escuro)
      },
    },
    // Um pouco de respiro vertical por linha (o "levemente mais arejado" do CD).
    MuiFormControlLabel: { styleOverrides: { root: { paddingTop: 6, paddingBottom: 6 } } },
  },
};
